import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, ShieldCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProgramIcon from "@/components/ProgramIcon";
import { IMAGES, PROGRAM_META, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

function Hero() {
  const { t } = useLang();
  const lines = t("home.heroLines");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  return (
    <section ref={ref} className="grain relative flex min-h-[86svh] items-start overflow-hidden bg-[#0C2E3A] sm:min-h-[80vh]" aria-label={t("home.heroAria")} data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 -z-0" aria-hidden="true">
        <img
          src={IMAGES.hero}
          alt=""
          className="h-[115%] w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0C2E3A]/75 via-[#0C2E3A]/50 to-[#0C2E3A]/15" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0C2E3A]/80 to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity }} className="relative mx-auto w-full max-w-7xl px-4 pb-28 pt-10 sm:px-6 sm:pt-8 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 inline-flex items-center gap-2 border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-sm"
          data-testid="hero-overline"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-[#E8A34C]" aria-hidden="true" />
          {t("home.heroOverline")}
        </motion.p>

        <h1 className="font-heading max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-heading">
          {lines.map((line, i) => (
            <span key={line} className="hero-line-mask">
              <motion.span
                className="hero-line-inner"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {i >= lines.length - 2 ? <span className="text-[#E8A34C]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200 sm:text-xl"
          data-testid="hero-subcopy"
        >
          {t("home.heroSub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
        >
          <Link
            to="/quote"
            className="group inline-flex items-center justify-center gap-2 bg-[#D97706] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#B45309] sm:w-auto"
            data-testid="request-quote-hero-btn"
          >
            {t("common.requestQuote")}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
          <a
            href={SITE.phone1Href}
            className="inline-flex items-center justify-center gap-2 border border-white/40 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,border-color] duration-200 hover:border-white hover:bg-white/10 sm:w-auto"
            data-testid="hero-call-btn"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {t("home.heroCall")}
          </a>
          <a
            href={SITE.phone2Href}
            className="inline-flex items-center justify-center gap-2 border border-white/40 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,border-color] duration-200 hover:border-white hover:bg-white/10 sm:w-auto"
            data-testid="hero-call-diana-btn"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {t("home.heroCallDiana")}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/60 md:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t("home.scroll")}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block h-8 w-px bg-white/40" />
      </motion.div>
    </section>
  );
}

function StatsBar() {
  const { t } = useLang();
  const stats = t("home.stats");
  return (
    <section className="relative z-10 -mt-16 px-4 sm:px-6 lg:px-8" aria-label={t("home.statsAria")} data-testid="stats-bar">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 sm:flex-row sm:gap-8">
        <Reveal delay={0}>
          <img src={IMAGES.anniversary30} alt={t("home.anniversaryAlt")} className="h-24 w-24 shrink-0 drop-shadow-lg sm:h-28 sm:w-28" data-testid="stats-anniversary-badge" />
        </Reveal>
        <div className="grid w-full flex-1 grid-cols-2 gap-px overflow-hidden bg-[#0F4C5C]/10 shadow-xl shadow-slate-900/5 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <Reveal key={stat.label} delay={i} className="bg-white p-6 sm:p-8">
            <p className="font-heading text-3xl font-extrabold tracking-tight text-[#0F4C5C] sm:text-4xl" data-testid={`stat-value-${i}`}>
              {stat.value}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-[#D97706]">{stat.label}</p>
            <p className="mt-2 text-sm text-slate-600">{stat.sub}</p>
          </Reveal>
        ))}
        </div>
      </div>
    </section>
  );
}

function Programs() {
  const { t } = useLang();
  return (
    <section className="bg-[#EEF6FC] py-16 sm:py-24 lg:py-28" aria-labelledby="programs-heading" data-testid="programs-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <Reveal>
            <p className="overline-label" data-testid="programs-overline">{t("home.programsOverline")}</p>
          </Reveal>
          <Reveal delay={1}>
            <h2 id="programs-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              {t("home.programsHeading")}
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-4 text-lg text-slate-600 sm:text-xl">
              {t("home.programsSub")}
            </p>
          </Reveal>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {PROGRAM_META.map((program, i) => (
            <Reveal key={program.id} delay={i % 4}>
              <article
                className="group flex h-full flex-col border border-[#0F4C5C]/10 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-[#0F4C5C]/30 hover:shadow-xl hover:shadow-[#0F4C5C]/10"
                data-testid={`program-card-${program.id}`}
              >
                <span className="mb-4 inline-flex h-11 w-11 items-center justify-center bg-[#EEF6FC] text-[#0F4C5C] transition-colors duration-300 group-hover:bg-[#0F4C5C] group-hover:text-white">
                  <ProgramIcon name={program.icon} />
                </span>
                <h3 className="font-heading text-lg font-semibold leading-tight text-slate-900">{t(`programs.${program.id}.title`)}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{t(`programs.${program.id}.desc`)}</p>
                <Link
                  to="/quote"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:text-[#D97706]"
                  data-testid={`program-quote-link-${program.id}`}
                >
                  {t("common.getQuote")} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={1}>
          <div className="mt-10 bg-[#D97706] p-7 text-white shadow-lg shadow-[#D97706]/20 sm:p-8" data-testid="premium-financing-note">
            <p className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">{t("home.premiumNote")}</p>
            <p className="mt-2 text-lg font-medium text-white/90">{t("home.premiumNoteSub")}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhoWeServe() {
  const { t } = useLang();
  const audiences = t("audiences");
  return (
    <section className="bg-[#FBF9F5] py-16 sm:py-24 lg:py-28" aria-labelledby="serve-heading" data-testid="who-we-serve-section">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal className="relative order-2 lg:order-1">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <img
              src={IMAGES.shoppingCenter}
              alt={t("home.serveImageAlt1")}
              className="h-[320px] w-full object-cover shadow-2xl sm:h-[400px] lg:h-[460px]"
              loading="lazy"
              data-testid="who-we-serve-image-1"
            />
            <img
              src={IMAGES.condo}
              alt={t("home.serveImageAlt2")}
              className="mt-8 h-[320px] w-full object-cover shadow-2xl sm:h-[400px] lg:h-[460px]"
              loading="lazy"
              data-testid="who-we-serve-image-2"
            />
          </div>
          <div className="absolute -bottom-6 left-4 bg-[#0F4C5C] px-6 py-4 text-white shadow-xl" data-testid="who-we-serve-badge">
            <p className="font-heading text-2xl font-extrabold">{t("home.triCounty")}</p>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-300">{t("home.triCountySub")}</p>
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <p className="overline-label" data-testid="serve-overline">{t("home.serveOverline")}</p>
          </Reveal>
          <Reveal delay={1}>
            <h2 id="serve-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              {t("home.serveHeading")}
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-4 text-lg leading-relaxed text-slate-600 sm:text-xl">
              {t("home.serveSub")}
            </p>
          </Reveal>
          <ul className="mt-8 grid gap-x-6 gap-y-3 sm:grid-cols-2" data-testid="audiences-list">
            {audiences.map((audience, i) => (
              <Reveal key={audience} delay={i % 6} as="li">
                <span className="flex items-start gap-2.5 text-sm font-medium text-slate-800">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C5C]" aria-hidden="true" />
                  {audience}
                </span>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function WhyAf() {
  const { t } = useLang();
  const chapters = t("why");
  return (
    <section className="grain relative bg-[#0F4C5C] py-16 text-white sm:py-24 lg:py-28" aria-labelledby="why-heading" data-testid="why-af-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-3xl">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-base font-extrabold uppercase tracking-[0.22em] text-[#E8A34C]" data-testid="why-overline">
              <span className="block h-px w-8 bg-[#E8A34C]" aria-hidden="true" />
              {t("home.whyOverline")}
            </p>
          </Reveal>
          <Reveal delay={1}>
            <h2 id="why-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              {t("home.whyHeading")}
            </h2>
          </Reveal>
        </div>
        <div className="space-y-0 border-t border-white/15">
          {chapters.map((chapter) => (
            <Reveal key={chapter.num} delay={0}>
              <div
                className="group grid items-baseline gap-2 border-b border-white/15 py-8 transition-colors duration-300 hover:bg-white/5 sm:grid-cols-[80px_240px_1fr] sm:gap-8 sm:px-4"
                data-testid={`why-chapter-${chapter.num}`}
              >
                <span className="font-heading text-3xl font-extrabold text-[#E8A34C]/70 transition-colors duration-300 group-hover:text-[#E8A34C] sm:text-4xl">
                  {chapter.num}
                </span>
                <h3 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">{chapter.title}</h3>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{chapter.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HurricaneBand() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden" aria-labelledby="hurricane-heading" data-testid="hurricane-band">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={IMAGES.tealBuilding} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[#1A365D]/55" />
      </div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <div>
          <Reveal>
            <p className="inline-flex items-center gap-2 text-base font-extrabold uppercase tracking-[0.22em] text-[#E8A34C]">
              <span className="block h-px w-8 bg-[#E8A34C]" aria-hidden="true" />
              {t("home.hurricaneOverline")}
            </p>
          </Reveal>
          <Reveal delay={1}>
            <h2 id="hurricane-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {t("home.hurricaneHeading")}
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-200">
              {t("home.hurricaneSub")}
            </p>
          </Reveal>
        </div>
        <Reveal delay={2} className="flex flex-col gap-3 lg:items-end">
          <Link
            to="/hurricane"
            className="inline-flex items-center justify-center gap-2 bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-[#1A365D] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#EEF6FC]"
            data-testid="hurricane-guide-btn"
          >
            {t("home.hurricaneGuideBtn")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a
            href={SITE.phone1Href}
            className="inline-flex items-center justify-center gap-2 border border-white/40 px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-white/10"
            data-testid="hurricane-call-btn"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> {t("home.hurricaneCallBtn")}
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function AboutTeaser() {
  const { t } = useLang();
  return (
    <section className="bg-white py-16 sm:py-24 lg:py-28" aria-labelledby="about-teaser-heading" data-testid="about-teaser-section">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.5fr] lg:gap-16 lg:px-8">
        <Reveal className="relative mx-auto w-full max-w-sm lg:max-w-none">
          <img
            src={IMAGES.armando}
            alt={t("home.aboutImageAlt")}
            className="w-full object-cover shadow-2xl"
            loading="lazy"
            data-testid="armando-teaser-image"
          />
          <div className="absolute -bottom-5 left-5 bg-[#D97706] px-5 py-3 text-white shadow-lg">
            <p className="font-heading text-xl font-extrabold leading-none">{t("home.yearsBadge")}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.18em]">{t("home.yearsBadgeSub")}</p>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="overline-label" data-testid="about-teaser-overline">{t("home.aboutOverline")}</p>
          </Reveal>
          <Reveal delay={1}>
            <h2 id="about-teaser-heading" className="font-heading mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              {t("home.aboutHeading")}
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <blockquote className="mt-6 border-l-4 border-[#D97706] pl-5 text-lg italic leading-relaxed text-slate-700" data-testid="armando-quote">
              {t("home.armandoQuote")}
            </blockquote>
          </Reveal>
          <Reveal delay={3}>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t("home.armandoCred")}
            </p>
          </Reveal>
          <Reveal delay={4}>
            <Link
              to="/about"
              className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:text-[#D97706]"
              data-testid="about-teaser-link"
            >
              {t("home.aboutLink")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function QuoteCta() {
  const { t } = useLang();
  return (
    <section className="bg-[#EEF6FC] py-16 sm:py-20" aria-labelledby="cta-heading" data-testid="quote-cta-section">
      <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-[1.5fr_1fr] lg:px-8">
        <Reveal>
          <h2 id="cta-heading" className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("home.ctaHeading")}
          </h2>
          <p className="mt-3 max-w-xl text-lg text-slate-600 sm:text-xl">
            {t("home.ctaSub")}
          </p>
        </Reveal>
        <Reveal delay={1} className="flex flex-col gap-3 sm:flex-row lg:justify-end">
          <Link
            to="/quote"
            className="inline-flex items-center justify-center gap-2 bg-[#0F4C5C] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1A365D]"
            data-testid="cta-quote-btn"
          >
            {t("common.requestQuote")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a
            href={SITE.phone1Href}
            className="inline-flex items-center justify-center gap-2 border-2 border-[#0F4C5C] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:bg-[#0F4C5C] hover:text-white"
            data-testid="cta-call-btn"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  usePageMeta(t("meta.home")[0], t("meta.home")[1]);
  return (
    <>
      <Hero />
      <StatsBar />
      <Marquee />
      <Programs />
      <WhoWeServe />
      <WhyAf />
      <HurricaneBand />
      <AboutTeaser />
      <QuoteCta />
    </>
  );
}
